import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { UseFilterActiveFilterTypedFilters } from "@/components/filter/use-filter.hook.tsx";
import { toEntries } from "@/helpers/utils.helper.ts";
import { ApiAnalyticsTimeOffBalanceSummaryReport } from "@/types/analytics/analytics.time-off.types.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiTimeOffCategoryType } from "@/types/time-off/time-off-policy.types.ts";
import { Table, Text } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface TimeOffBalancesSummaryTableProps {
  report: ApiAnalyticsTimeOffBalanceSummaryReport | undefined;
  isLoading: boolean;
  typedFilters: UseFilterActiveFilterTypedFilters;
  onApplySorting: (sorting: ApiSorting) => void;
}

interface VMTableRow {
  employee: ApiEmployee;
  balances: (number | null)[];
}

export function TimeOffBalancesSummaryTable({
  report,
  isLoading,
  typedFilters,
  onApplySorting,
}: TimeOffBalancesSummaryTableProps) {
  const { t } = useTranslation();

  const computedReportRows = useMemo(() => {
    if (!report) return [];

    const rows: VMTableRow[] = [];

    for (const row of report.report.rows) {
      rows.push({
        employee: row.employee,
        balances: row.balance_list,
      });
    }

    return rows;
  }, [report]);

  const columns = useMemo<ColumnDef<VMTableRow>[]>(() => {
    const inner: ColumnDef<VMTableRow>[] = [];

    inner.push({
      id: EmployeeColumnId.employee_name,
      header: t("core:full_name"),
      accessorKey: EmployeeColumnId.employee_name,
      cell: (cell) => <EmployeeItem enablePreview={true} employee={cell.row.original.employee} />,
      ...ColumnSizes.employee,
      enableSorting: true,
      sortDescFirst: false,
    });
    if (report?.config && report.config.categories) {
      for (const [categoryIndex, category] of toEntries(report.config.categories)) {
        inner.push({
          id: `balance_category_id|${category.id}`,
          accessorKey: category.id.toString(),
          header: category.title,
          cell: (cell) => (
            <Text>
              {category.category_type === ApiTimeOffCategoryType.flexible
                ? "-"
                : cell.row.original.balances[categoryIndex]}
            </Text>
          ),
          ...ColumnSizes.status,
          enableSorting: category.category_type !== ApiTimeOffCategoryType.flexible,
          sortDescFirst: false,
        });
      }
    }
    return inner;
  }, [report, computedReportRows]);

  return (
    <Table
      data={computedReportRows || []}
      columns={columns}
      showSkeleton={isLoading}
      stickyHeader={true}
      sorting={typedFilters.sorting}
      onSortUpdated={onApplySorting}
    />
  );
}
